/**
 * **从语义色派生出令牌表** —— 主题作者少写一半的字段。
 *
 * ## 为什么要有「派生」这一步
 *
 * 主题作者真正想说的是「强调色是橙的、分类色是这几个」，而不是
 * 「`--np-accent-soft` 是 `rgba(240,160,75,0.16)`」。让他手写后者有两个坏处：
 *
 *   1. 改一次强调色要同步改四处，**漏一处就出现一个不搭的颜色**；
 *   2. 手写的 alpha 值互相不一致（现状就是：`0.16` / `0.22` / `0.14` / `0.26`
 *      四个值散在 `theme.css` 里，谁都说不清为什么不一样）。
 *
 * 所以定一条规则：**能推出来的就别让人写。** `tokens` 仍然保留 ——
 * 它是逃生舱，派生结果会被它覆盖。
 *
 * ## ⚠️ 派生是「补缺」不是「覆盖」
 *
 * 顺序永远是：**派生 → 主题的 `tokens`**。这样「我就要那个奇怪的 alpha」
 * 永远能办到，而默认值是合理的一整套。
 */
import { hexToHsv, hsvToHex, isHexColor, withAlpha } from '../types/color'
import type { ThemeDensity, ThemeMotion, ThemePalette } from '../types/theme'

/** 令牌统一加这个前缀 */
export const TOKEN_PREFIX = '--np-'

/**
 * 补前缀。
 *
 * ⚠️ 手写 JSON 主题时漏前缀是**最常见的错**，而它的后果是
 * 「静默失效」—— 变量名不对，CSS 只是不生效，不报任何错。
 * 所以两种写法都收，而不是报错。
 */
export function tokenKey(key: string): string {
  const k = key.trim()
  return k.startsWith('--') ? k : TOKEN_PREFIX + k.replace(/^np-/, '')
}

/**
 * 分类 id → 颜色。
 *
 * ⚠️ **这一步取代了原来的「未知分类一律 `utility` 灰」**。
 * 旧行为的问题：装一个新节点包，它所有分类的节点头都是同一个 `#454545`，
 * 于是「分类色」这个信息**直接消失**（用户看到一片灰）。
 *
 * 现在是确定性色相旋转：同 id 永远同色（可复现、可缓存），
 * 不同 id 大概率不同色。饱和度/明度固定在一个「不像荧光笔」的区间。
 */
export function deriveCategoryColor(categoryId: string, scheme: 'dark' | 'light'): string {
  let hash = 0
  for (let i = 0; i < categoryId.length; i += 1) {
    hash = (hash * 31 + categoryId.charCodeAt(i)) >>> 0
  }
  /* 黄金角步进 —— 让相邻的 hash 也落在色环上相隔较远的位置 */
  const hue = (hash * 137.508) % 360
  return scheme === 'dark'
    ? hsvToHex({ h: hue, s: 0.28, v: 0.47 })
    : hsvToHex({ h: hue, s: 0.34, v: 0.72 })
}

/** 强调色的三个角色：前景 / 弱底 / 实心 */
export function deriveAccent(accent: string): Record<string, string> {
  return {
    '--np-accent': accent,
    '--np-accent-soft': withAlpha(accent, 0.16),
    '--np-accent-solid': accent,
    /* 更弱的一档，给「悬停高亮」这种不该抢戏的地方 */
    '--np-accent-faint': withAlpha(accent, 0.08),
  }
}

/* ══════════════════════════════════════════════════════════════════
   密度
   ══════════════════════════════════════════════════════════════════ */

/**
 * 密度档位 → 一组尺寸令牌。
 *
 * ## ⚠️ 为什么控件高度走**缩放系数**而不是直接给 px
 *
 * 第一版这里直接产出 `--np-ctl-h-lg: 28px` 之类，然后被**内联写在 `<html>` 上**。
 * 而 `theme.css` 里有一条可访问性下限：
 *
 * ```css
 * @media (pointer: coarse) { :root { --np-ctl-h-lg: 40px } }
 * ```
 *
 * 内联样式**赢过样式表规则**（同元素上），所以主题一旦声明了密度，
 * 那条下限就**静默失效** —— 手机上所有按钮、菜单项、工具格都按桌面尺寸渲染。
 * 实测反馈就是这个：「怎么移动端好多 UI 都变了」。
 *
 * 修法是把密度表达成 `--np-density-scale`，由 `theme.css` 去乘：
 *
 * ```css
 * --np-ctl-h-lg: calc(30px * var(--np-density-scale));
 * @media (pointer: coarse) {
 *   --np-ctl-h-lg: max(40px, calc(30px * var(--np-density-scale)));
 * }
 * ```
 *
 * 这样密度仍然生效，而下限**永远说了算**。教训是：
 * **凡是和可访问性下限冲突的值，都不要走内联变量那条路。**
 *
 * ⚠️ 行高 / 控件高 / 头部高**不在这里** —— 它们的触摸下限声明在
 * `.np-node` 上（元素自己的声明赢过继承），所以直接给 px 是安全的。
 */
/**
 * 动效系数。
 *
 * ⚠️ 这里**只产出系数**，时长令牌（`--np-motion-fast` 等）由 `theme.css`
 * 去乘。理由和 `DENSITY_TOKENS` 一模一样 —— 见那段注释，
 * 以及 `--np-ctl-h-lg` 那条 `max()` 的下限。
 *
 * ⚠️ 这三个值会通过 `data-np-motion` 属性作用到 `:root` 上，
 * **不是**内联变量（`applyTheme` 走的是属性通道）。
 */
export const MOTION_TOKENS: Record<ThemeMotion, string> = {
  off: '0',
  subtle: '0.65',
  normal: '1',
}

export const DENSITY_TOKENS: Record<ThemeDensity, Record<string, string>> = {
  compact: {
    /* 0.9 ≈ 原来的 20/24/28 ÷ 22/26/30 */
    '--np-density-scale': '0.9',
    '--np-row-h': '19px',
    '--np-body-pad-y': '5px',
    '--np-widget-h': '16px',
    '--np-font-size': '10.5px',
    '--np-title-font-size': '11px',
    '--np-header-h': '23px',
  },
  normal: {
    '--np-density-scale': '1',
    '--np-row-h': '22px',
    '--np-body-pad-y': '6px',
    '--np-widget-h': '17px',
    '--np-font-size': '11px',
    '--np-title-font-size': '11.5px',
    '--np-header-h': '26px',
  },
  comfortable: {
    /* 1.2 ≈ 原来的 26/31/36 ÷ 22/26/30 */
    '--np-density-scale': '1.2',
    '--np-row-h': '28px',
    '--np-body-pad-y': '9px',
    '--np-widget-h': '23px',
    '--np-font-size': '12.5px',
    '--np-title-font-size': '13px',
    '--np-header-h': '32px',
  },
}

/* ══════════════════════════════════════════════════════════════════
   总入口
   ══════════════════════════════════════════════════════════════════ */

/**
 * 把一个调色板展开成完整的令牌表。
 *
 * @param palette 主题声明的语义色（可以只写一部分）
 * @param scheme  底色方向 —— **影响未知分类的派生色**（浅底要更暗才看得清）
 * @param density 密度档位，不写就不产出尺寸令牌
 */
export function deriveTokens(
  palette: ThemePalette | undefined,
  scheme: 'dark' | 'light' = 'dark',
  density?: ThemeDensity,
): Record<string, string> {
  const out: Record<string, string> = {}

  if (palette?.accent && isHexColor(palette.accent)) {
    Object.assign(out, deriveAccent(palette.accent))
  }
  if (palette?.danger && isHexColor(palette.danger)) {
    out['--np-danger'] = palette.danger
  }

  for (const [id, color] of Object.entries(palette?.categories ?? {})) {
    if (!isHexColor(color)) continue
    out[`--np-cat-${id}`] = color
  }
  for (const [type, color] of Object.entries(palette?.sockets ?? {})) {
    if (!isHexColor(color)) continue
    out[`--np-socket-${type}`] = color
  }
  for (const [name, color] of Object.entries(palette?.severity ?? {})) {
    if (!color || !isHexColor(color)) continue
    out[`--np-severity-${name}`] = color
  }

  if (density) Object.assign(out, DENSITY_TOKENS[density])

  return out
}

/** 强调色的可读前景（在强调色实心块上写字时用） */
export function readableOn(color: string): string {
  const hsv = hexToHsv(color)
  if (!hsv) return '#ffffff'
  return hsv.v > 0.62 ? '#1a1a1a' : '#ffffff'
}
